import _ from 'lodash';

import { getPagePathname } from './childrenWindowsUtils';
import { isTrue } from './commonUtils';
import { isMobileDevice } from './windowDimensionsUtils';
import { PANEL_CONFIGURATIONS } from '../constants/PanelLayoutConstants';
import { ENTITY_DISPLAY } from '../constants/Urls';
import { FORM_KEYS, SS_CONTROL_TYPE } from '../form-designer/constants/form-manager.constants';

const waitForElement = (selector) =>
  new Promise((resolve) => {
    let timer = undefined;
    const element = document.querySelector(selector);

    if (element) {
      return resolve(element);
    }

    const observer = new MutationObserver(() => {
      const element = document.querySelector(selector);

      if (element) {
        clearTimeout(timer);
        resolve(element);
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    timer = setTimeout(() => {
      // eslint-disable-next-line no-console
      console.log(`${selector} not found in the document`);

      resolve(null);
      observer.disconnect();
    }, 2000);
  });

const clickOnTab = (tabId) => {
  const instanceId = `${tabId}#[0]`;
  const tabNode = document.getElementById(instanceId);

  if (tabNode) {
    tabNode.click();
    return;
  }

  if (['development', 'test'].includes(process.env.NODE_ENV)) {
    throw new Error(
      `Cannot find a tab with instanceId: ${instanceId}. It might be placed inside a table`
    );
  }
};

export const scrollIntoEditor = async (id) => {
  await waitForElement(`[id="${id}"]`);

  const control = document.getElementById(id);

  if (control) {
    control.scrollIntoView({ block: 'center' });
  }
};

const openCollapsedPanelsAndFocusControl = (control) => {
  let parent = control.parentElement;
  const collapsedPanels = [];

  while (parent.id !== 'root') {
    if (parent.dataset.isInsideBlade === 'true') {
      parent = document.getElementById(parent.dataset.parentGridInstanceid);

      if (!parent) {
        return;
      }
    }

    if (parent.dataset.collapsed === 'true') {
      collapsedPanels.push(parent);
    }

    parent = parent.parentElement;
  }

  for (const panel of collapsedPanels) {
    // open all collasped parent panels
    panel.firstElementChild.click();
  }

  if (collapsedPanels.length) {
    // focus again
    setTimeout(() => {
      control.focus();
    }, 200);
  }
};

export function navigateToControl(id) {
  const control = document.getElementById(id);

  if (!control) {
    return;
  }

  if (control.type === 'hidden') {
    const sibling = control.previousElementSibling ||
      control.nextElementSibling || { focus: () => {} };
    sibling.focus();
  } else {
    control.focus();
    openCollapsedPanelsAndFocusControl(control);
  }

  if (control.getAttribute('role') === 'tab') {
    control.click();
  }
}

const getMimeType = (filename = '') => {
  const mimeTypes = {
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    html: 'text/html',
    zip: 'application/zip',
    xml: 'application/xml',
    txt: 'text/plain',
    json: 'application/json',
    csv: 'text/csv',
    pdf: 'application/pdf',
  };

  const extension = filename
    .split('.')
    .pop()
    .toLowerCase();

  return mimeTypes[extension] || 'application/octet-stream';
};

export function downloadFile(filename, content, mimeTypeNumber) {
  const mimeType = {
    0: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    1: 'text/html',
    2: 'application/zip',
    3: 'application/xml',
  };
  const element = document.createElement('a');
  const url = window.URL.createObjectURL(
    new Blob([content], {
      type: mimeTypeNumber ? mimeType[mimeTypeNumber] : getMimeType(filename),
    })
  );
  // const url = 'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8,' + encodeURIComponent(content)
  element.setAttribute('href', url);
  element.setAttribute('download', filename);

  element.style.display = 'none';
  document.body.appendChild(element);

  element.click();

  document.body.removeChild(element);
}

export const parseHtmlToText = (html) => {
  if (_.isNil(html)) {
    return '';
  }
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.innerHTML;
};

export const expandContainer = (containerInfo) => {
  const { controlType, containerId, requiredSubGrids, formModel } = containerInfo;

  switch (controlType) {
    case SS_CONTROL_TYPE.SINGLE_TAB: {
      clickOnTab(containerId);
      return;
    }

    case SS_CONTROL_TYPE.GRID: {
      const subGridId = formModel[containerId].childrenNodes.find(
        (id) => formModel[id].controlType === SS_CONTROL_TYPE.SUB_GRID
      );

      const subGridInstances = requiredSubGrids
        .filter((subGridInstanceId) => subGridInstanceId.includes(subGridId))
        .sort();

      // find the 1-st visible required subgrid and expand it
      for (const instanceId of subGridInstances) {
        const pencilIconSpanNode = document.querySelector(
          `[data-pencil-icon-subgrid-instanceid='${instanceId}']`
        );

        if (!pencilIconSpanNode) {
          continue;
        }

        const isSubGridExpanded = isTrue(
          pencilIconSpanNode.getAttribute('data-pencil-icon-subgrid-expanded')
        );

        if (isSubGridExpanded) {
          return;
        }

        const pencilIconButtonNode = pencilIconSpanNode.querySelector('button');
        pencilIconButtonNode.click();

        return;
      }

      return;
    }

    default:
      return;
  }
};

export const expandContainersAndNavigateToControl = async (containersInfo, controlInstanceId) => {
  for (let i = 0; i < containersInfo.length; i++) {
    const { id, subGridInstanceId } = containersInfo[i];

    if (!subGridInstanceId) {
      clickOnTab(id);
      continue;
    }

    const pencilIconSpanNode = await waitForElement(
      `[data-pencil-icon-subgrid-instanceid='${subGridInstanceId}']`
    );

    if (!pencilIconSpanNode) {
      return;
    }

    const pencilIconButtonNode = pencilIconSpanNode.querySelector('button');
    pencilIconButtonNode.click();
  }

  await waitForElement(`[id="${controlInstanceId}"]`);

  navigateToControl(controlInstanceId);
};

export const clickEditRowButtonAndNavigateToControl = async (rowId, controlInstanceId) => {
  const rowPencilIconNode = document.querySelector(`[data-pencil-icon-rowid='${rowId}']`);

  if (rowPencilIconNode) {
    rowPencilIconNode.querySelector('button').click();

    await waitForElement(`[id="${controlInstanceId}"]`);
  }

  navigateToControl(controlInstanceId);
};

export const handleWarningClick = async (warning) => {
  const { instanceId, containerInfo, containersInfo, rowId } = warning;

  if (!instanceId) {
    return;
  }

  if (isMobileDevice() && getPagePathname() === ENTITY_DISPLAY) {
    document.getElementById('mobileStepFormTab').click();
    await waitForElement('[id="formManager"]');
  }

  if (containerInfo) {
    // grouped warnings
    expandContainer(containerInfo);
    return;
  }

  if (containersInfo) {
    // warnings created by validation rules
    // warnings on mobile devices
    expandContainersAndNavigateToControl(containersInfo, instanceId);
    return;
  }

  if (rowId) {
    clickEditRowButtonAndNavigateToControl(rowId, instanceId);
    return;
  }

  navigateToControl(instanceId);
};

export const handleToastNavigation = async (toastNavInfo, panelConfig) => {
  const { containerInfo, formKey } = toastNavInfo;
  const isDetailForm = formKey === FORM_KEYS.DETAIL;

  if (!isMobileDevice() && isDetailForm && getPagePathname() === ENTITY_DISPLAY) {
    const shouldOpenDetailFormPanel = [
      PANEL_CONFIGURATIONS.RIGHT_COLLAPSED,
      PANEL_CONFIGURATIONS.ONLY_MIDDLE,
    ].includes(panelConfig);

    if (shouldOpenDetailFormPanel) {
      const buttonToOpenDetailForm = document.querySelector(
        "[ui-ta='StepFormPanel'] [ui-ta='ThreePanelLayout ArrowButton left']"
      );

      if (!buttonToOpenDetailForm) {
        return;
      }

      buttonToOpenDetailForm.click();
      await waitForElement("[ui-ta='DetailFormPanel']");
    }
  }

  if (isMobileDevice() && isDetailForm && getPagePathname() === ENTITY_DISPLAY) {
    document.getElementById('mobileDetailFormTab').click();
    await waitForElement('[id="formManager"]');
  }

  expandContainer(containerInfo);
};

// should match the values in .blinkingControl class (src\assets\css\styles.css)
const ANIMATION_DURATION = 750;
const ANIMATION_ITERATION_COUNT = 3;

const scrollControlIntoView = (control) => {
  control.scrollIntoView({ block: 'center', inline: 'center' });
  control.classList.add('blinkingControl');

  setTimeout(() => {
    control.classList.remove('blinkingControl');
  }, ANIMATION_DURATION * ANIMATION_ITERATION_COUNT + 500);
};

const getFormContainer = (formKey) => {
  if (formKey === FORM_KEYS.DETAIL) {
    return document.querySelector('[ui-ta="DetailFormPanel"]');
  }

  return (
    document.querySelector('[ui-ta="stepFormContainer"]') ||
    document.querySelector('[ui-ta="StepFormPanel"]')
  );
};

export const navigateToControlFCM = (id, formKey, formModel) => {
  let control = null;

  if (formKey === FORM_KEYS.DETAIL) {
    // check if control is inside header section
    control = document.getElementById('header-section').querySelector(`[data-ss-id="${id}"]`);

    if (control) {
      scrollControlIntoView(control);
      return;
    }
  }

  const formContainer = getFormContainer(formKey);
  control = formContainer.querySelector(`[data-ss-id="${id}"]`);

  if (control) {
    scrollControlIntoView(control);
    return;
  }

  let parentId = formModel[id].parent;

  while (!control && parentId !== 'root') {
    control = formContainer.querySelector(`[data-ss-id="${parentId}"]`);
    parentId = formModel[parentId].parent;
  }

  if (control) {
    scrollControlIntoView(control);
  }
};
